import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import type {IDivisionsStore} from "data/stores/divisions/divisions.store";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IStandingsStore, IUserStanding} from "data/stores/standings/standings.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {
	IDivisionSlot,
	ILightPredictionsStore,
} from "data/stores/light_predictions/light_predictions.store";
import {eq, orderBy, take} from "lodash";
import {ChangeEvent} from "react";
import type {IRankingStore} from "data/stores/ranking/ranking.store";
import {LP_PATH} from "data/constants";
import {useNavigate} from "react-router-dom";
import {ModalType} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";

interface IProps {
	navigate: ReturnType<typeof useNavigate>;
}

export interface IOffSeasonController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;
	isLoading: boolean;

	get userNfcStandings(): IDivisionSlot[];
	get userAfcStandings(): IDivisionSlot[];
	get nfcStandings(): IUserStanding[];
	get afcStandings(): IUserStanding[];
	get isRealStandingsActive(): boolean;

	setRealStandingsActive: (event: ChangeEvent<HTMLInputElement>) => void;
}

@injectable()
export class OffSeasonController implements IOffSeasonController {
	@observable isLoading: boolean = false;
	@observable _isRealStandingsActive: boolean = false;
	@observable private _navigate?: ReturnType<typeof useNavigate>;

	get userNfcStandings() {
		return this._lpStore.nfcPredictions;
	}

	get userAfcStandings() {
		return this._lpStore.afcPredictions;
	}

	get nfcStandings() {
		return this.createStandingArr(
			this._standingsStore.standings,
			this._conferenceStore.nfcConferenceId
		);
	}

	get afcStandings() {
		return this.createStandingArr(
			this._standingsStore.standings,
			this._conferenceStore.afcConferenceId
		);
	}

	get isRealStandingsActive() {
		return this._isRealStandingsActive;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.DivisionsStore) private _divisionStore: IDivisionsStore,
		@inject(Bindings.TournamentsStore) private _tournamentStore: ITournamentsStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.RankingStore) private _rankingStore: IRankingStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	private createStandingArr = (standings: IUserStanding[], conferenceId: number) => {
		const standingByRound = standings.filter((it) => {
			if (it.roundId === this._roundStore.actualStandingsRoundId) {
				const squad = this._squadStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, conferenceId);
			}
			return false;
		});

		return take(orderBy(standingByRound, ["conferenceRank"], "asc"), 7);

		// return take(this._orderStandingsStore.getSortedStandings(standingByRound, conferenceId), 7);
	};

	private setIsLoading(value: boolean): void {
		this.isLoading = value;
	}

	@action setRealStandingsActive = (event: ChangeEvent<HTMLInputElement>) => {
		this._isRealStandingsActive = event.target.checked;
	};

	showOffSeasonModal = () => {
		this._modalStore.showModal(ModalType.OFF_SEASON, {
			message: "",
		});
	};

	async init(param: IProps) {
		this._navigate = param.navigate;

		this.setIsLoading(true);

		await Promise.all([
			this._roundStore.fetchRounds(),
			this._squadStore.fetchSquads(),
			this._conferenceStore.fetchConferences(),
			this._divisionStore.fetchDivisions(),
			this._tournamentStore.fetchTournaments(),
			this._standingsStore.fetchStandings(),
		]);

		if (this._userStore.isAuthorized) {
			await this._lpStore.getPredictions();
			await this._rankingStore.getUserRank();
		}

		if (!this._roundStore.isOffSeason) {
			this._navigate?.(LP_PATH, {replace: true});
		}

		this.setIsLoading(false);

		if (!this._userStore.isAuthorized) {
			this.showOffSeasonModal();
		}
	}
}
